import * as React from 'react';
import { useCallback, useState } from 'react';
import cn from 'classnames';

import ErrorBoundary from '../../../../../ErrorBoundary/ErrorBoundary';
import { default as Button } from '../../../../../Button';
import WarningModal from '../../../../../WarningModal';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../../../theme';
import { useConfig } from '../../../../../context';
import { CheckinFatalError, CheckinUnavailableSeatMapError } from './CheckinErrors';
import { SafeHtml } from '@shared/ui';

interface Props {
	lastName: string;
	ticketNumber: string;
}

const CheckinErrorBoundary: React.FC<Props> = props => {
	const { t } = useTranslation('Checkin');
	const { CheckinErrorBoundary: css } = useTheme('Checkin');

	const [checkinError, setCheckinError] = useState<string>(null);

	// const oldEngineURL
	const {
		global: { locale, reserveEngines }
	} = useConfig();

	const checkinUrl = reserveEngines?.checkin
		? `${reserveEngines?.checkin}?lastName=${props.lastName}&ticketNumber=${props.ticketNumber}&lang=${locale}`
		: null;

	const fallbackRenderer = useCallback(
		error => {
			if (error instanceof CheckinFatalError) {
				let errorText = t(
					reserveEngines?.checkin
						? `But don't worry, all of our systems have been duplicated several times. You can register on the backup version of the site.`
						: 'An unexpected error has occurred during the start of the check-in process. Please try again later or contact a customer support service.'
				);

				if (error instanceof CheckinUnavailableSeatMapError) {
					errorText = error.message;
				}

				setCheckinError(errorText);
			} else {
				return null;
			}

			return (
				<WarningModal
					className={css.modal}
					isOpen={!!checkinError}
					title={t('Oops, something went wrong')}
					content={
						<div>
							<SafeHtml tag="div" html={checkinError} />

							<div className={css.buttons}>
								{reserveEngines?.siteUrl && (
									<Button className={css.button} onClick={() => window.open(reserveEngines.siteUrl)}>
										{t('Go to home')}
									</Button>
								)}
								<Button className={css.button} onClick={() => window.location.reload()}>
									{t('Try again')}
								</Button>

								{checkinUrl && (
									<Button
										className={cn(css.button, css.active)}
										onClick={() => window.open(checkinUrl, '_blank')}
									>
										{t('Go')}
									</Button>
								)}
							</div>
						</div>
					}
				/>
			);
		},
		[checkinError, setCheckinError]
	);

	return <ErrorBoundary renderFallback={fallbackRenderer}>{props.children}</ErrorBoundary>;
};

export default CheckinErrorBoundary;
